import { useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Camera, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import api from "@/lib/api";
import { euro, formatApiErrorDetail } from "@/lib/format";
import { todayIso, fmtDay } from "@/lib/cantieri";
import { SPESA_CAT } from "@/components/cantieri/spesaCategorie";

const blank = (categoria = "materiali") => ({ categoria, data: todayIso(), importo: "", descrizione: "", fornitore: "" });

const CatPicker = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-1.5 col-span-2 sm:col-span-4" data-testid="spesa-cantiere-categories">
    {Object.entries(SPESA_CAT).map(([k, { label, icon: Icon }]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`spesa-cantiere-cat-${k}`}
        className={`inline-flex items-center gap-1.5 min-h-[40px] [@media(pointer:coarse)]:min-h-[44px] px-3 rounded-full border text-sm font-medium transition-colors ${
          value === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
        <Icon className="w-4 h-4" /> {label}
      </button>
    ))}
  </div>
);

// Spese del cantiere: materiali, pranzi, carburante, noleggio attrezzi, altro. Con foto dello scontrino.
export const SpeseCantierePanel = ({ cantiere, onChanged }) => {
  const [f, setF] = useState(blank());
  const [busy, setBusy] = useState(false);
  const [ocr, setOcr] = useState(false);
  const [ask, setAsk] = useState(null);
  const fileRef = useRef(null);
  const spese = [...(cantiere.spese || [])].sort((a, b) => (b.data || "").localeCompare(a.data || ""));
  const perCat = cantiere.spese_per_categoria || {};

  const add = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/cantieri/${cantiere.id}/spese`, { ...f, importo: Number(f.importo) });
      toast.success(`Spesa registrata: ${SPESA_CAT[f.categoria].label} ${euro(Number(f.importo))}`);
      setF(blank(f.categoria)); onChanged(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const scan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcr(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: d = {} } = await api.post("/ocr/receipt", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 150000 });
      setF((p) => ({ ...p, importo: d.importo || p.importo, fornitore: d.fonte || p.fornitore, data: d.data || p.data, descrizione: p.descrizione || d.descrizione || "" }));
      toast.success("Scontrino letto: controlla i dati e premi Aggiungi");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Non riesco a leggere lo scontrino"); }
    finally { setOcr(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const del = async (s) => {
    if (ask !== s.id) { setAsk(s.id); return; }
    setAsk(null);
    onChanged((await api.delete(`/cantieri/${cantiere.id}/spese/${s.id}`)).data);
    toast.success("Spesa eliminata");
  };

  return (
    <div className="space-y-4" data-testid="spese-cantiere-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 rounded-xl border border-border p-3">
        <CatPicker value={f.categoria} onChange={(categoria) => setF({ ...f, categoria })} />
        <DateInput value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} data-testid="spesa-cantiere-date-input" />
        <Input type="number" min="0" step="0.01" inputMode="decimal" placeholder="Importo €" value={f.importo} onChange={(e) => setF({ ...f, importo: e.target.value })} data-testid="spesa-cantiere-amount-input" />
        <Input placeholder="Descrizione (es. cemento, pranzo squadra)" value={f.descrizione} onChange={(e) => setF({ ...f, descrizione: e.target.value })} className="col-span-2" data-testid="spesa-cantiere-desc-input" />
        <Input placeholder="Fornitore / negozio" value={f.fornitore} onChange={(e) => setF({ ...f, fornitore: e.target.value })} className="col-span-2" data-testid="spesa-cantiere-supplier-input" />
        <input ref={fileRef} type="file" accept="image/*,.pdf" onChange={scan} className="hidden" data-testid="spesa-cantiere-scan-input" />
        <Button type="button" variant="secondary" onClick={() => fileRef.current?.click()} disabled={ocr} data-testid="spesa-cantiere-scan-button">
          {ocr ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Camera className="w-4 h-4 mr-1.5" />} {ocr ? "Leggo…" : "Foto scontrino"}
        </Button>
        <Button onClick={add} disabled={busy || !(Number(f.importo) > 0) || !f.data} data-testid="spesa-cantiere-add-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Aggiungi</>}
        </Button>
      </div>

      {Object.keys(perCat).length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="spese-cantiere-totals">
          {Object.entries(perCat).map(([k, v]) => (
            <span key={k} className={`rounded-full px-3 py-1 text-sm ${SPESA_CAT[k]?.tone || SPESA_CAT.altro.tone}`} data-testid={`spese-cantiere-total-${k}`}>
              {SPESA_CAT[k]?.label || k} <b>{euro(v)}</b>
            </span>
          ))}
          <span className="rounded-full px-3 py-1 text-sm border border-border" data-testid="spese-cantiere-total">Totale <b>{euro(cantiere.totale_spese)}</b></span>
        </div>
      )}

      <div className="rounded-xl border border-border divide-y divide-border">
        {spese.map((s) => {
          const cat = SPESA_CAT[s.categoria] || SPESA_CAT.altro;
          const Icon = cat.icon;
          return (
            <div key={s.id} className="flex items-center gap-3 px-3 py-2.5 text-sm" data-testid={`spesa-cantiere-row-${s.id}`}>
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${cat.tone}`}><Icon className="w-4 h-4" /></span>
              <span className="flex-1 min-w-0">
                <span className="font-medium block truncate">{s.descrizione || cat.label}</span>
                <span className="text-xs text-muted-foreground block truncate">{[fmtDay(s.data), cat.label, s.fornitore].filter(Boolean).join(" · ")}</span>
              </span>
              <span className="font-semibold tabular-nums">{euro(s.importo)}</span>
              {ask === s.id
                ? <Button variant="destructive" size="sm" onClick={() => del(s)} onBlur={() => setAsk(null)} data-testid={`spesa-cantiere-delete-confirm-${s.id}`}>Elimina?</Button>
                : <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive" onClick={() => del(s)} aria-label="Elimina" data-testid={`spesa-cantiere-delete-${s.id}`}><Trash2 className="w-4 h-4" /></Button>}
            </div>
          );
        })}
        {!spese.length && <div className="p-6 text-center text-sm text-muted-foreground">Nessuna spesa registrata per questo cantiere.</div>}
      </div>
    </div>
  );
};

export default SpeseCantierePanel;
